import React, { useContext } from 'react';
import { chain, size, sortBy, omit, isEmpty, keys, difference } from 'underscore';
import PropTypes from 'prop-types';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { Target } from '@bingads-webui-react/dimensions-target';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import computerIcon from './styles/media/desktop.svg';
import tabletIcon from './styles/media/tablet.svg';
import smartphoneIcon from './styles/media/mobile.svg';
import connectedTVIcon from './styles/media/ctv.svg';

export const DeviceKeys = {
  Computers: 'Computers',
  Tablets: 'Tablets',
  Smartphones: 'Smartphones',
  ConnectedTVs: 'ConnectedTVs',
};

export const Devices = {
  [DeviceKeys.Computers]: _TL_('Desktop'),
  [DeviceKeys.Tablets]: _TL_('Tablet'),
  [DeviceKeys.Smartphones]: _TL_('Mobile'),
  [DeviceKeys.ConnectedTVs]: _TL_('CTV'),
};

export const BidStrategyToDevices = {
  ManualCpm: [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones],
  ManualCpv: [DeviceKeys.ConnectedTVs],
};

const sortDevices = devices => sortBy(devices, device => Devices[device]);
const iconStyle = {
  width: 32,
  height: 32,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
};

const containerStyle = {
  width: 96,
  height: 96,
  border: '1px solid #E1DFDD',
  borderRadius: '4px',

  '&.is-checked': {
    border: '1px solid #0078D4',
    '&:hover': {
      border: '1px solid #005A9E',
    },
  },

  '&:hover': {
    border: '1px solid #323130',
  },

  '& input[type="checkbox"]': {
    width: '100%',
  },

  '& label': {
    padding: 0,
    flex: 1,
    alignSelf: 'stretch',
  },
};

const getStyle = () => ({
  computer: {
    ...containerStyle,
    '& .icon': {
      backgroundImage: `url("${computerIcon}")`,
      ...iconStyle,
    },
  },
  tablet: {
    ...containerStyle,
    '& .icon': {
      backgroundImage: `url("${tabletIcon}")`,
      ...iconStyle,
    },
  },
  smartphones: {
    ...containerStyle,
    '& .icon': {
      backgroundImage: `url("${smartphoneIcon}")`,
      ...iconStyle,
    },
  },
  connectedTVs: {
    ...containerStyle,
    '& .icon': {
      backgroundImage: `url("${connectedTVIcon}")`,
      ...iconStyle,
    },
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  pickerContainer: {
    '& div.dimensions': {
      display: 'flex',
      gap: '4px',
      margin: '3px 0 3px 3px',
      '&>span.fui-Checkbox': {
        flexDirection: 'column',

        '& .fui-Checkbox__indicator': {
          alignSelf: 'flex-end',
          margin: '4px',
        },
      },
    },
  },
});

export const formatDevice = (device, i18n) => (Devices[device] ? i18n.getString(Devices[device]) : device);

export const formatDevices = ({ devices, i18n, isCTVSupported }) => {
  if ((isCTVSupported && size(devices) === size(Devices)) ||
    (!isCTVSupported && size(devices) === size(Devices) - 1)
  ) {
    return i18n.getString(_TL_('All devices'));
  }

  return chain(sortDevices(devices))
    .map(device => formatDevice(device, i18n))
    .join(i18n.getString(_TL_(', ')))
    .value();
};

export const getDeviceDetails = (devices, isCTVSupported, i18n) => {
  if (isEmpty(devices)) {
    return '';
  }

  if ((!isCTVSupported && devices.length === 3) || (isCTVSupported && devices.length === 4)) {
    return i18n.getString(_TL_('Targeting: All devices'));
  }

  const formattedDevices = devices.map(device => Devices[device] || device).join(i18n.getString(_TL_(', ')));

  return i18n.getString(_TL_('{{formattedDevices}}'), { formattedDevices });
};

export const Device = ({
  i18n,
  devices,
  deviceRestrictions,
  isCTVSupported,
  hideDeselectAll,
  validationPropsField,
  defaultSelectAll,
}) => {
  const classes = useDefaultStyles(getStyle);
  const activity = useContext(ActivityContext);
  const supportedDevices = isCTVSupported ? Devices : omit(Devices, DeviceKeys.ConnectedTVs);
  const isAnyDeviceSupported = isEmpty(difference(keys(supportedDevices), deviceRestrictions));

  return (<Target
    i18n={i18n}
    description={isAnyDeviceSupported ? i18n.getString(_TL_('Customize your device targeting below.')) : ''}
    formatter={formatDevice}
    dimensions={devices}
    availableDimensions={supportedDevices}
    disabledDimensions={deviceRestrictions}
    validationProps={{ field: validationPropsField }}
    hideDeselectAll={hideDeselectAll}
    logger={activity ? { log: activity.signal } : undefined}
    targetClasses={{
      [DeviceKeys.Computers]: classes.computer,
      [DeviceKeys.Tablets]: classes.tablet,
      [DeviceKeys.Smartphones]: classes.smartphones,
      [DeviceKeys.ConnectedTVs]: classes.connectedTVs,
    }}
    containerClass={classes.pickerContainer}
    renderLabel={(options) => {
      const { label } = options;

      return (
        <div className={classes.iconContainer}>
          <div className="icon" />
          <div>{label}</div>
        </div>
      );
    }}
    defaultSelectAll={defaultSelectAll}
  />);
};

Device.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  devices: PropTypes.arrayOf(PropTypes.string).isRequired,
  deviceRestrictions: PropTypes.arrayOf(PropTypes.string),
  isCTVSupported: PropTypes.bool,
  hideDeselectAll: PropTypes.bool,
  validationPropsField: PropTypes.string,
  defaultSelectAll: PropTypes.bool,
};

Device.defaultProps = {
  deviceRestrictions: [],
  isCTVSupported: true,
  hideDeselectAll: true,
  validationPropsField: 'deviceTypes',
  defaultSelectAll: false,
};
